// RODAPÉ "Aurora" (rodape = 7): rodapé do modelo "Aurora" (claro, arejado, cantos bem
// arredondados). Abre com uma FAIXA-CONVITE em cartão arredondado (raio 28px, cor2),
// sobreposta ~50% para fora do rodapé: frase curta + botão de WhatsApp/telefone (só com
// telefone cadastrado). Abaixo, grade de 4 colunas:
//  (a) logo + nome/CRECI + redes sociais em botões circulares;
//  (b) Navegação = Início, transações, menus do tenant e blog;
//  (c) Contato = telefones (WhatsApp quando marcado), e-mail revelado no clique (não vai
//      cru no HTML, como no rodapé 6) e endereço;
//  (d) Institucional = páginas institucionais do tenant (Quem somos / Trabalhe conosco /
//      Privacidade…), Política de privacidade e Termos de uso.
// Faixa "Nossas filiais" (multi-filiais, FiliaisRodape) e linha final fina com direitos
// autorais, CRECI/CNPJ, html_rodape e selo ImobTotal (gate copyright/EsconderRodapeSobressai).
// Fundos: cor3 (conteúdo) + cor6 (linha final), texto cor7 com contraste CALCULADO
// (ensureReadableTextColor) — funciona claro ou escuro. "Pesquisas mais populares" como os
// demais rodapés (hidePopular). Default export = ErrorBoundary com fallback (padrão Autoral).
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { FaFacebook, FaInstagram, FaLinkedin, FaPhoneAlt, FaTiktok, FaWhatsapp, FaYoutube } from 'react-icons/fa';
import { ensureReadableTextColor, isColorLight, MaskPhone, removerAcentos } from '../../../utils/FunctionsGlobals';
import { trackMetaEvent } from '../../../utils/metaPixel';
import { resolveSiteWhatsAppUrl } from '../../../utils/site2524WhatsApp';
import { appendCodsiteQuery } from '../../../utils/url';
import { useAuth, useSiteRegiao } from '../../context/Info';
import { trackLead } from '../../whatsAppButton/WhatsAppButton';
import type { RodapeSectionProps } from '../../registry/types';
import FiliaisRodape from '../filiais-rodape/FiliaisRodape';
import Popular from '../populares/Popular';
import LinhaSeoRodape from '../rodape-seo/LinhaSeoRodape';
import { SeloImobtotalItem } from '../selo-imobtotal/SeloImobtotal';
import auHome from '../aurora-secoes/AuroraHome.module.css';
import { varsAurora } from '../aurora/aurora';
import styles from './AuroraFooter.module.css';

type NavItem = { label: string; href: string; external?: boolean };

// Páginas do tenant que são "institucionais" (vão pra coluna Institucional, não pra Navegação).
const INSTITUCIONAL_RE = /quem somos|sobre n|a empresa|nossa historia|institucional|trabalhe|privacidade|lgpd|termos/;
const ehInstitucional = (titulo: unknown): boolean =>
    INSTITUCIONAL_RE.test(removerAcentos(String(titulo ?? '')).toLowerCase().replace(/\s+/g, ' '));
const ehLegal = (titulo: unknown): boolean => /privacidade|lgpd|termos/i.test(String(titulo ?? ''));

const buildWhatsHref = (site: any, phone?: string) => {
    const clean = (phone || '').replace(/\D/g, '');
    if (!clean) return '';
    const text =
        site?.texto_botao_whats ||
        `Olá, estou visitando o site da ${site?.Dados?.empresa || ''}, gostaria de um atendimento personalizado. Pode me ajudar?`;
    return `https://api.whatsapp.com/send?phone=55${clean}&text=${encodeURIComponent(text)}`;
};

const AuroraFooterInner: React.FC<RodapeSectionProps> = ({ site: siteProp, origin, codsite, allRodape, hidePopular, allCities, allCitiesTipes }) => {
    // MULTI-REGIÃO: contatos/endereço trocados pelos da região ativa quando houver.
    const site = useSiteRegiao(siteProp);
    const { allDataHome } = useAuth();
    const router = useRouter();
    const codsiteQuery = Array.isArray(router.query?.codsite) ? router.query?.codsite[0] : router.query?.codsite;
    const [isMounted, setIsMounted] = useState(false);
    const [emailVisivel, setEmailVisivel] = useState(false);
    useEffect(() => setIsMounted(true), []);
    const withCodsite = (href: string) => (isMounted ? appendCodsiteQuery(href, codsite ?? codsiteQuery, origin) : href);
    const trackFooter = (nome: string, cat: string) => () => trackMetaEvent(site, 'ViewContent', { content_name: nome, content_category: cat });

    // ---- cores: fundo cor3 / linha final cor6; texto cor7 quando contrasta, senão calculado ----
    const corFundo = site?.cor3 || '#f3f4f6';
    const corTexto = ensureReadableTextColor(site?.cor7 || (isColorLight(corFundo) ? site?.cor11 : site?.cor13), corFundo) || '#1c2230';
    const corTitulo = ensureReadableTextColor(site?.cor2, corFundo, 3.2, corTexto, corTexto) || corTexto;
    const corBarra = site?.cor6 || corFundo;
    const corBarraTexto = ensureReadableTextColor(site?.cor7 || (isColorLight(corBarra) ? site?.cor11 : site?.cor13), corBarra) || corTexto;
    const corConvite = site?.cor2 || '#2563eb';
    const corConviteTexto = ensureReadableTextColor(isColorLight(corConvite) ? site?.cor11 : site?.cor13, corConvite) || '#ffffff';
    // botão do convite: claro sobre a cor2 (texto calculado sobre o branco).
    const corBotaoTexto = ensureReadableTextColor(corConvite, '#ffffff', 3.0, '#1c2230', '#1c2230') || '#1c2230';
    // borda/fundo sutis derivados do texto (funciona claro ou escuro).
    const sutil = isColorLight(corFundo) ? 'rgba(15, 23, 42, 0.1)' : 'rgba(255, 255, 255, 0.16)';

    // ---- navegação: Início + transações + menus do tenant (não institucionais) + blog ----
    const menusDisponiveis = (allDataHome?.menus || []).filter((m: any) => m?.Disponivel && m?.titulo);
    const paraItem = (m: any): NavItem => ({
        label: m.titulo,
        href: m?.link ? m.link : `/menu/${m.titulo}`,
        external: Boolean(m?.link && !String(m.link).startsWith('/')),
    });
    const navegacao: NavItem[] = [{ label: 'Início', href: '/' }];
    (site?.transacoes || []).forEach((t: string) => {
        if (!t) return;
        const low = String(t).toLowerCase();
        navegacao.push({ label: `Imóveis para ${low === 'aluguel' ? 'alugar' : low === 'venda' ? 'comprar' : low}`, href: `/imoveis/${low}/-/-/-/-` });
    });
    menusDisponiveis.filter((m: any) => !ehInstitucional(m.titulo)).slice(0, 7).forEach((m: any) => navegacao.push(paraItem(m)));
    if (allDataHome?.blogs?.[0]?.id) navegacao.push({ label: 'Blog', href: '/blog' });

    // Política/termos na LINHA FINAL, ao lado do © (Daniel 15/10: "onde estão os termos de uso e política?" — como
    // no Atelier e no Larissa). A coluna Institucional fica só com as páginas do tenant e some quando não há nenhuma.
    // Política/termos padrão da plataforma só quando o tenant não tem a própria página.
    const institucional: NavItem[] = menusDisponiveis
        .filter((m: any) => ehInstitucional(m.titulo) && !ehLegal(m.titulo))
        .slice(0, 5)
        .map(paraItem);
    const legais: NavItem[] = menusDisponiveis.filter((m: any) => ehLegal(m.titulo)).map(paraItem);
    if (!legais.some((i) => /privacidade|lgpd/i.test(i.label))) legais.push({ label: 'Política de privacidade', href: '/politica' });
    if (!legais.some((i) => /termos/i.test(i.label))) legais.push({ label: 'Termos de uso', href: '/termos' });

    // ---- contato ----
    const phones = [
        { tel: site?.Dados?.tel1, nome: site?.Dados?.tel1_nome, whats: site?.Dados?.tel1_whats },
        { tel: site?.Dados?.tel2, nome: site?.Dados?.tel2_nome, whats: site?.Dados?.tel2_whats },
        { tel: site?.Dados?.tel3, nome: site?.Dados?.tel3_nome, whats: site?.Dados?.tel3_whats },
        { tel: site?.Dados?.tel4, nome: site?.Dados?.tel4_nome, whats: site?.Dados?.tel4_whats },
    ].filter((p) => p.tel);
    const whatsPhone = phones.find((p) => p.whats);
    const ctaPhone = whatsPhone || phones[0];
    const ctaIsWhats = Boolean(whatsPhone);
    // WhatsApp passa pelo redirecionador por site (cs2524), como no rodapé clássico; demais sites: o mesmo link.
    const ctaHref = ctaPhone ? (ctaIsWhats ? resolveSiteWhatsAppUrl(site, buildWhatsHref(site, ctaPhone.tel)) : `tel:+55${(ctaPhone.tel || '').replace(/\D/g, '')}`) : '';
    const email = site?.Dados?.email1 || site?.Dados?.email2;
    const enderecoLinha = [site?.Dados?.endereco, [site?.Dados?.bairro, site?.Dados?.cidade].filter(Boolean).join(' - '), site?.Dados?.cep].filter(Boolean).join(', ');

    const socials = [
        { href: site?.Dados?.instagram, Icon: FaInstagram, rede: 'Instagram' },
        { href: site?.Dados?.facebook, Icon: FaFacebook, rede: 'Facebook' },
        { href: site?.Dados?.youtube, Icon: FaYoutube, rede: 'Youtube' },
        { href: site?.Dados?.tiktok, Icon: FaTiktok, rede: 'Tiktok' },
        { href: site?.Dados?.Linkedin, Icon: FaLinkedin, rede: 'Linkedin' },
    ].filter((s) => s.href);

    const logoSrc = site?.url_logo ? `${site?.url_logo}?v=${site?.contador_marca || 0}` : '';
    const empresa = site?.Dados?.empresa || '';
    const creci = site?.Dados?.creci;
    const cnpj = site?.Dados?.cnpj;

    const renderLink = (item: NavItem, idx: number, cat: string) =>
        item.external ? (
            <li key={idx}>
                <a href={item.href} className={styles.link} rel="noopener noreferrer" onClick={trackFooter(item.label, cat)}>
                    {item.label}
                </a>
            </li>
        ) : (
            <li key={idx}>
                <Link prefetch={false} href={withCodsite(item.href)} className={styles.link} onClick={trackFooter(item.label, cat)}>
                    {item.label}
                </Link>
            </li>
        );

    return (
        <>
            {/* "Pesquisas mais populares" com o visual da HOME do modelo em todas as páginas (Daniel 15/10: no
                /empreendimento saía o bloco genérico). O estilo vive nas regras `.home` da home do modelo; a casca
                repete a classe (e as variáveis) aqui — na home fica aninhada na mesma classe, sem efeito. */}
            {!hidePopular && (
                <div className={auHome.home} style={varsAurora(site)}>
                    <Popular site={site} allRodape={allRodape} codsite={codsite} origin={origin} />
                </div>
            )}
            {/* BUILDER: âncora estável do bloco "rodape". */}
            <footer className={styles.footer} data-builder-section="rodape">
                {/* faixa-convite: cartão sobreposto ~50% para fora do rodapé */}
                <div className={styles.conviteWrap}>
                    <div className={styles.convite} style={{ background: corConvite, color: corConviteTexto }}>
                        <p className={styles.conviteFrase}>Vamos encontrar o seu próximo imóvel?</p>
                        {ctaPhone ? (
                            <a
                                className={styles.conviteBotao}
                                href={ctaHref}
                                style={{ color: corBotaoTexto }}
                                rel="noopener noreferrer"
                                target={ctaIsWhats ? '_blank' : undefined}
                                title={ctaPhone.nome || (ctaIsWhats ? 'WhatsApp' : 'Telefone')}
                                onClick={() => {
                                    trackLead(site, null); // whatsapp_click no GTM/GA, como no rodapé clássico (sem imóvel = sem Meta)
                                    trackMetaEvent(site, 'Contact', {
                                        content_name: 'Convite do rodapé',
                                        contact_method: ctaIsWhats ? 'whatsapp' : 'phone',
                                        content_category: 'footer_contact',
                                    });
                                }}
                            >
                                {ctaIsWhats ? <FaWhatsapp aria-hidden="true" /> : <FaPhoneAlt aria-hidden="true" />}
                                {ctaIsWhats ? 'Falar no WhatsApp' : `Ligar ${MaskPhone(ctaPhone.tel)}`}
                            </a>
                        ) : null}
                    </div>
                </div>

                <div className={styles.conteudo} style={{ background: corFundo, color: corTexto }}>
                    <div className={`${styles.grade}${institucional.length ? '' : ` ${styles.gradeTres}`}`}>
                        {/* (a) marca */}
                        <div className={`${styles.col} ${styles.colMarca}`}>
                            {logoSrc ? (
                                <Link prefetch={false} href={withCodsite('/')} className={styles.logoLink} title={`Imobiliária ${empresa}`}>
                                    <Image src={logoSrc} alt={`Imobiliária ${empresa}`} className={styles.logo} width={220} height={80} unoptimized />
                                </Link>
                            ) : (
                                <span className={styles.logoTexto} style={{ color: corTitulo }}>
                                    {empresa}
                                </span>
                            )}
                            {empresa || creci ? (
                                <p className={styles.marcaLinha}>
                                    {empresa}
                                    {empresa && creci ? ' · ' : ''}
                                    {creci ? `CRECI ${creci}` : ''}
                                </p>
                            ) : null}
                            {socials.length > 0 ? (
                                <div className={styles.redes}>
                                    {socials.map((s, idx) => (
                                        <a
                                            key={idx}
                                            href={s.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.rede}
                                            style={{ borderColor: sutil, color: corTexto }}
                                            title={`Navegar para o ${s.rede} da imobiliária`}
                                            aria-label={s.rede}
                                        >
                                            <s.Icon aria-hidden="true" />
                                        </a>
                                    ))}
                                </div>
                            ) : null}
                        </div>

                        {/* (b) navegação */}
                        <div className={styles.col}>
                            <span className={styles.colTitulo} style={{ color: corTitulo }}>
                                Navegação
                            </span>
                            <ul className={styles.lista}>{navegacao.map((item, idx) => renderLink(item, idx, 'footer_navigation'))}</ul>
                        </div>

                        {/* (c) contato */}
                        <div className={styles.col}>
                            <span className={styles.colTitulo} style={{ color: corTitulo }}>
                                Contato
                            </span>
                            <ul className={styles.lista}>
                                {phones.map((p, idx) => {
                                    const href = p.whats ? resolveSiteWhatsAppUrl(site, buildWhatsHref(site, p.tel)) : `tel:+55${(p.tel || '').replace(/\D/g, '')}`;
                                    return (
                                        <li key={idx}>
                                            <a
                                                className={`${styles.link} ${styles.contato}`}
                                                href={href}
                                                rel="noopener noreferrer"
                                                target={p.whats ? '_blank' : undefined}
                                                onClick={() => {
                                                    trackLead(site, null); // whatsapp_click (GTM/GA), como no rodapé clássico
                                                    trackFooter('Telefone do rodapé', 'footer_contact')();
                                                }}
                                            >
                                                {p.whats ? <FaWhatsapp aria-hidden="true" style={{ color: corTitulo }} /> : <FaPhoneAlt aria-hidden="true" style={{ color: corTitulo }} />}
                                                <span>
                                                    {MaskPhone(p.tel)}
                                                    {p.nome ? ` — ${p.nome}` : ''}
                                                </span>
                                            </a>
                                        </li>
                                    );
                                })}
                                {email ? (
                                    <li>
                                        {emailVisivel ? (
                                            <a className={styles.link} href={`mailto:${email}`}>
                                                {email}
                                            </a>
                                        ) : (
                                            <button type="button" className={`${styles.link} ${styles.verEmail}`} onClick={() => setEmailVisivel(true)}>
                                                Ver e-mail
                                            </button>
                                        )}
                                    </li>
                                ) : null}
                                {enderecoLinha ? <li className={styles.endereco}>{enderecoLinha}</li> : null}
                            </ul>
                        </div>

                        {/* (d) institucional — só com páginas do tenant */}
                        {institucional.length ? (
                            <div className={styles.col}>
                                <span className={styles.colTitulo} style={{ color: corTitulo }}>
                                    Institucional
                                </span>
                                <ul className={styles.lista}>{institucional.map((item, idx) => renderLink(item, idx, 'footer_institutional'))}</ul>
                            </div>
                        ) : null}
                    </div>

                    {/* MULTI-FILIAIS: endereços de TODAS as filiais (null fora do modo popup). */}
                    <FiliaisRodape site={site} fundo={corFundo} cor={corTexto} />

                    {/* SEO: cidades · tipos · empreendimentos do rodapé clássico, em linha discreta (16/10) */}
                    <LinhaSeoRodape
                        site={site}
                        allCities={allCities}
                        allCitiesTipes={allCitiesTipes}
                        allRodape={allRodape}
                        withCodsite={withCodsite}
                        jaTem={[...navegacao, ...institucional, ...legais].map((i) => i.href)}
                        className={styles.linhaSeo}
                    />
                </div>

                {/* linha final */}
                <div className={styles.linhaFinal} style={{ background: corBarra, color: corBarraTexto }}>
                    <div className={styles.linhaFinalInner}>
                        <span className={styles.legal}>
                            © {new Date().getFullYear()} {empresa}. Todos os direitos reservados.
                            {creci ? ` · CRECI ${creci}` : ''}
                            {cnpj ? ` · CNPJ ${cnpj}` : ''}
                        </span>
                        <ul className={styles.legais}>{legais.map((item, idx) => renderLink(item, idx, 'footer_institutional'))}</ul>
                        {site?.html_rodape ? <span className={styles.htmlRodape} dangerouslySetInnerHTML={{ __html: site?.html_rodape }} /> : null}
                        {/* Selo ImobTotal: respeita copyright + EsconderRodapeSobressai (gate dentro do componente). */}
                        <SeloImobtotalItem site={site} host={origin} alinhar="esquerda" linhaPropria fonte="13px" />
                    </div>
                </div>
            </footer>
        </>
    );
};

class AuroraFooter extends React.Component<RodapeSectionProps, { hasError: boolean }> {
    constructor(props: RodapeSectionProps) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: degrada pro rodapé padrão sem quebrar a página.
    }
    render() {
        const { fallback, site } = this.props;
        if (this.state.hasError || !site) return fallback ?? null;
        return <AuroraFooterInner {...this.props} />;
    }
}

export default AuroraFooter;
